import type { AnalyticsEvent, BaseEventData, ConsentCategory } from "./types";
import { hasConsent } from "./consent";
import { getAllVariants } from "./ab-testing";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  let id = sessionStorage.getItem("uk_session_id");
  if (!id) {
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem("uk_session_id", id);
  }
  return id;
}

function getBaseData(locale: "sv" | "en"): BaseEventData {
  const variants = getAllVariants();
  return {
    timestamp: Date.now(),
    session_id: getSessionId(),
    locale,
    ...(Object.keys(variants).length > 0 ? { ab_variant: JSON.stringify(variants) } : {}),
  };
}

// Events that require marketing consent (FB Pixel, Google Ads conversions)
const MARKETING_EVENTS = new Set(["booking_confirmed", "booking_submitted", "phone_call_click"]);

function getRequiredConsent(eventName: string): ConsentCategory {
  if (MARKETING_EVENTS.has(eventName)) return "marketing";
  return "analytics";
}

export function pushEvent(event: AnalyticsEvent, locale: "sv" | "en"): void {
  if (typeof window === "undefined") return;

  const requiredConsent = getRequiredConsent(event.event);
  // Always push analytics events (they respect GTM Consent Mode internally)
  // Marketing events are gated by consent
  if (requiredConsent === "marketing" && !hasConsent("marketing")) {
    // Still push to dataLayer — GTM Consent Mode will block the tags
    // This ensures events are queued if consent is granted later
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...getBaseData(locale),
    ...event,
  });

  // Debug logging in development
  if (process.env.NODE_ENV === "development") {
    console.log("[Analytics]", event.event, event);
  }
}
